import terms from "../assets/images/navbar/terms.png";
import redCancel from "../assets/images/order/red-cancel.png";
import greenTick from "../assets/images/order/green-tick.png";
import car from "../assets/images/icons/car.png";
import star from "../assets/images/navbar/star.png";
import orangeStar from "../assets/images/navbar/orange-star.png";
import { Order as OrderType } from "../utils/types";
import { useState } from "react";
import CancelOrderModal from "../modal/CancelOrderModal";
import OrderDetailsModal from "../modal/OrderDetailsModal";
import DeliveryOtpModal from "../modal/DeliveryOtpModal";
import { restaurantOrderService } from "../services";

const Order = ({
  order,
  selectedOrderIndex,
  index,
  tab,
  onStatusUpdate,
  onTabChange,
}: {
  order: OrderType;
  selectedOrderIndex: number;
  index: number;
  tab: string;
  onStatusUpdate?: () => void;
  onTabChange?: (newTab: string) => void;
}) => {
  const rating = 4;
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isDeliveryOtpModalOpen, setIsDeliveryOtpModalOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const handleCancelClick = () => {
    setIsCancelModalOpen(true);
  };

  const handleCancelConfirm = async (reason: string) => {
    try {
      setIsUpdating(true);
      await restaurantOrderService.updateOrderStatus(order._id, "cancelled");
      console.log("Order cancelled with reason:", reason);
      if (onStatusUpdate) {
        onStatusUpdate();
      }
    } catch (error) {
      console.error("Error cancelling order:", error);
      alert("Failed to cancel order. Please try again.");
    } finally {
      setIsUpdating(false);
      setIsCancelModalOpen(false);
    }
  };

  const handleStatusUpdate = async (newStatus: string) => {
    try {
      setIsUpdating(true);
      await restaurantOrderService.updateOrderStatus(order._id, newStatus);
      console.log("Order status updated to:", newStatus);
      
      // Switch to the appropriate tab after status update
      if (onTabChange) {
        onTabChange(newStatus);
      }
      
      if (onStatusUpdate) {
        onStatusUpdate();
      }
    } catch (error) {
      console.error("Error updating order status:", error);
      alert("Failed to update order status. Please try again.");
    } finally {
      setIsUpdating(false);
    }
  };

  // Format time from ISO string to readable format
  const formatTime = (isoString: string) => {
    const date = new Date(isoString);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  // Format full date
  const formatFullDate = (isoString: string) => {
    const date = new Date(isoString);
    return date.toLocaleDateString("en-US", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  // Get first item for display
  const firstItem = order.items[0];

  return (
    <div
      className={`rounded-[16px] border dark:border-gray-700 p-[12px] mt-4 bg-white dark:bg-gray-800 transition-colors ${
        selectedOrderIndex === index ? "border-2 border-[#FF8A00]" : ""
      }`}
    >
      <div className="flex justify-between items-center border-b-2 dark:border-gray-700 pb-[12px]">
        <div className="flex gap-[8px] items-center">
          <div className="flex items-center gap-4">
            <div className="h-[75px] w-[75px] rounded-[12px] bg-gray-200 flex items-center justify-center">
              <span className="text-2xl">🍽️</span>
            </div>
          </div>
          <div className="">
            <p className="text-[#050505] dark:text-gray-200 font-Urbanist text-[14px] font-medium leading-[20px]">
              {order.deliveryAddress.fullName}
            </p>
            <p className="text-[#6A6A6A] dark:text-gray-400 font-Urbanist text-[10px] font-normal leading-[17px]">
              {order.deliveryAddress.addressLine1}, {order.deliveryAddress.city}
              <span className="ml-2">{formatTime(order.createdAt)}</span>
            </p>
          </div>
        </div>
        <div>
          {tab === "out_for_delivery" ? (
            <h6 className="text-[#FF8A00] font-Urbanist text-[15.692px] font-medium leading-[26.154px]">
              02:05
            </h6>
          ) : (
            <h6 className="text-[#FF8A00] font-Urbanist text-[15.692px] font-medium leading-[26.154px]">
              02:05
            </h6>
          )}
        </div>
      </div>

      <div
        className={`flex justify-between items-center py-[12px] border-b-2 border-dashed dark:border-gray-700`}
      >
        <p className="text-[#050505] dark:text-gray-200 font-Urbanist text-[16px] font-medium leading-[20px]">
          {firstItem.quantity} x {firstItem.dishName}
          {order.items.length > 1 && ` +${order.items.length - 1} more`}
        </p>
        <p className="text-[#050505] dark:text-gray-200 font-Urbanist text-[16px] font-medium leading-[20px]">
          ₹{order.totalAmount.toFixed(2)}
        </p>
      </div>

      {(tab === "new" ||
        tab === "confirmed" ||
        tab === "preparing" ||
        tab === "ready_for_pickup") && (
        <div className="flex items-center gap-3 my-[10px] overflow-x-auto">
          <button 
            onClick={(e) => {
              e.stopPropagation();
              setIsDetailsModalOpen(true);
            }}
            className="w-[27%] flex items-center px-3 py-[18px] md:py-1.5 bg-[#F5F5F5] dark:bg-gray-700 rounded-md border border-gray-200 dark:border-gray-600 text-[#050505] dark:text-gray-200 font-Urbanist text-[12px] font-normal whitespace-nowrap"
          >
            <img src={terms} alt="terms" className="h-[15px] w-[15px] dark:invert" />
            <span className="ml-1">All Details</span>
          </button>

          <div className="border dark:border-gray-700 rounded-[8px] bg-[#F5F5F5] dark:bg-gray-700 w-[66%] md:w-[85%]">
            <button className="px-3 py-1.5 text-[#FF8A00] font-Urbanist text-[10px] font-medium whitespace-nowrap leading-[15px]">
              COOKING NOTE
            </button>
            <button className="py-1.5 text-[#6A6A6A] dark:text-gray-400 font-Urbanist text-[12px] font-normal whitespace-nowrap">
              “Please Add Extra Salt and Avoid garlic”
            </button>
          </div>
        </div>
      )}

      {tab === "previous" && (
        <div className="flex justify-between items-center my-[10px]">
          <div className="flex gap-[9px] items-center">
            <div>
              <p className="text-[#6A6A6A] font-Urbanist text-[10px] font-normal leading-[17px]">
                Order Placed on {formatFullDate(order.createdAt)}
              </p>

              <p className="text-[#6A6A6A] font-Urbanist text-[10px] font-normal leading-[17px]">
                {order.orderStatus.charAt(0).toUpperCase() +
                  order.orderStatus.slice(1)}
              </p>
            </div>
          </div>

          <div className="flex gap-1 items-center">
            <span className="text-[#050505] font-[Urbanist] text-[12px] font-medium leading-[25px]">
              Rated
            </span>
            {Array.from({ length: 5 }, (_, index) => (
              <img
                key={index}
                src={index < rating ? orangeStar : star}
                alt={index < rating ? "filled star" : "empty star"}
                className="h-[15px] w-[15px]"
              />
            ))}
          </div>
        </div>
      )}

      {tab === "new" ? (
        <div className="flex gap-[12px]">
          <button
            className="flex justify-center items-center border-2 py-[12px] px-[9px] mt-[10px] rounded-[8px] gap-[8px] cursor-pointer w-1/2 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={(e) => {
              e.stopPropagation();
              handleStatusUpdate("confirmed");
            }}
            disabled={isUpdating}
          >
            <img
              src={greenTick}
              alt="green tick"
              className="h-[24px] w-[24px]"
            />
            <p className="text-[#0d110c] dark:text-white font-Urbanist text-[16px] font-medium leading-[15px] -mt-[5px]">
              {isUpdating ? "Accepting..." : "Accept Order"}
            </p>
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleCancelClick();
            }}
            className="flex justify-center items-center border-2 py-[12px] px-[9px] mt-[10px] rounded-[8px] gap-[8px] cursor-pointer w-1/2 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isUpdating}
          >
            <img
              src={redCancel}
              alt="red cancel"
              className="h-[24px] w-[24px]"
            />
            <p className="text-[#F30] font-Urbanist text-[16px] font-medium leading-[15px] -mt-[5px]">
              Cancel Order
            </p>
          </button>
        </div>
      ) : tab === "confirmed" ? (
        <div className="flex gap-[12px]">
          <button
            className="flex justify-center items-center border-2 py-[12px] px-[9px] mt-[10px] rounded-[8px] gap-[8px] cursor-pointer w-1/2 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={(e) => {
              e.stopPropagation();
              handleStatusUpdate("preparing");
            }}
            disabled={isUpdating}
          >
            <img src={car} alt="car" className="h-[24px] w-[24px]" />
            <p className="text-[#ffad33] font-Urbanist text-[16px] font-medium leading-[15px] -mt-[5px]">
              {isUpdating ? "Updating..." : "Start Preparing"}
            </p>
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleCancelClick();
            }}
            className="flex justify-center items-center border-2 py-[12px] px-[9px] mt-[10px] rounded-[8px] gap-[8px] cursor-pointer w-1/2 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isUpdating}
          >
            <img
              src={redCancel}
              alt="red cancel"
              className="h-[24px] w-[24px]"
            />
            <p className="text-[#F30] font-Urbanist text-[16px] font-medium leading-[15px] -mt-[5px]">
              Cancel Order
            </p>
          </button>
        </div>
      ) : tab === "preparing" ? (
        <div className="flex gap-[12px]">
          <button
            className="flex justify-center items-center border-2 py-[12px] px-1 md:px-[9px] mt-[10px] rounded-[8px] gap-[8px] cursor-pointer w-1/2 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={(e) => {
              e.stopPropagation();
              handleStatusUpdate("ready_for_pickup");
            }}
            disabled={isUpdating}
          >
            <img src={car} alt="car" className="h-[24px] w-[24px]" />
            <p className="text-[#ffa233] font-Urbanist text-[16px] font-medium leading-[15px] -mt-[5px]">
              {isUpdating ? "Updating..." : "Ready for Pickup"}
            </p>
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleCancelClick();
            }}
            className="flex justify-center items-center border-2 py-[12px] px-[9px] mt-[10px] rounded-[8px] gap-[8px] cursor-pointer w-1/2 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isUpdating}
          >
            <img
              src={redCancel}
              alt="red cancel"
              className="h-[24px] w-[24px]"
            />
            <p className="text-[#F30] font-Urbanist text-[16px] font-medium leading-[15px] -mt-[5px]">
              Cancel Order
            </p>
          </button>
        </div>
      ) : tab === "ready_for_pickup" ? (
        <div className="flex gap-[12px]">
          <button
            className="flex justify-center items-center border-2 py-[12px] px-1 md:px-[9px] mt-[10px] rounded-[8px] gap-[8px] cursor-pointer w-1/2 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={(e) => {
              e.stopPropagation();
              setIsDeliveryOtpModalOpen(true);
            }}
            disabled={isUpdating}
          >
            <img src={car} alt="car" className="h-[24px] w-[24px]" />
            <p className="text-[#ffa233] font-Urbanist text-[13px] md:text-[16px] font-medium leading-[15px] -mt-[5px]">
              Out for Delivery
            </p>
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleCancelClick();
            }}
            className="flex justify-center items-center border-2 py-[12px] px-[9px] mt-[10px] rounded-[8px] gap-[8px] cursor-pointer w-1/2 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isUpdating}
          >
            <img
              src={redCancel}
              alt="red cancel"
              className="h-[24px] w-[24px]"
            />
            <p className="text-[#F30] font-Urbanist text-[16px] font-medium leading-[15px] -mt-[5px]">
              Cancel Order
            </p>
          </button>
        </div>
      ) : (
        ""
      )}

      {/* Cancel Order Modal */}

      {/* Order Details Modal */}
      <OrderDetailsModal
        isOpen={isDetailsModalOpen}
        onClose={() => setIsDetailsModalOpen(false)}
        order={order}
      />
      
      {/* Delivery OTP Modal */}
      <DeliveryOtpModal
        isOpen={isDeliveryOtpModalOpen}
        onClose={() => setIsDeliveryOtpModalOpen(false)}
        expectedOtp={order.outForDeliveryOtp?.toString() || ""}
        onVerifySuccess={() => handleStatusUpdate("out_for_delivery")}
      />
      
      <CancelOrderModal
        isOpen={isCancelModalOpen}
        onClose={() => setIsCancelModalOpen(false)}
        onConfirm={handleCancelConfirm}
        orderNumber={order._id}
      />
    </div>
  );
};

export default Order;
